import { monthlyFrom, planMonthly } from "@car/api/finance-plans";
import { breadcrumbJsonLd, vehicleJsonLd } from "@car/api/seo";
import { fillVehicleText, menuLinks } from "@car/api/site-content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@car/ui/components/accordion";
import { Badge } from "@car/ui/components/badge";
import { Button, buttonVariants } from "@car/ui/components/button";
import { DirectionProvider } from "@car/ui/components/direction";
import { Image, useImageUrl } from "@car/ui/components/image";
import { FormAlert, useAppForm } from "@car/ui/components/form";
import { NativeSelect, NativeSelectOption } from "@car/ui/components/native-select";
import { Separator } from "@car/ui/components/separator";
import { Table, TableBody, TableCell, TableRow } from "@car/ui/components/table";
import { ToggleGroup, ToggleGroupItem } from "@car/ui/components/toggle-group";
import { mobileText, requiredText } from "@car/ui/lib/form-rules";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  AutomotiveBattery01Icon,
  Calendar03Icon,
  Call02Icon,
  CarAlertIcon,
  CheckmarkCircle02Icon,
  DashboardSpeed01Icon,
  FuelStationIcon,
  GitCompareIcon,
  Location01Icon,
  PaintBoardIcon,
  PlayIcon,
  SecurityCheckIcon,
  Timer01Icon,
  Wrench02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";

import { NotFoundPage } from "@/components/not-found-page";
import { RequestSuccess } from "@/components/request-success";
import { TestDriveDialog } from "@/components/test-drive-dialog";
import { MobileNavigation, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import {
  bodyLabels,
  financeCalculator,
  fuelLabels,
  linkAttrs,
  lines,
  loadSiteContent,
  monthlyFromText,
  plateKindLabels,
  plateKindPrice,
  plateKinds,
  platePriceTitle,
  platePriceWords,
  priceAmount,
  primaryPlateKind,
  primaryPlateText,
  rateNote,
  specGroups,
  specKeys,
  specLabels,
  telHref,
  useSiteContent,
  type PlateKind,
  type SitePlan,
  type SiteVehicle,
} from "@/lib/content";
import {
  faDate,
  faNumber,
  faPercent,
  faPlainNumber,
  faToman,
  faTomanWords,
  toEnDigits,
} from "@/lib/format";
import { readAttribution } from "@/lib/attribution";
import { pageMeta, siteUrl } from "@/lib/seo";
import { orpc } from "@/utils/orpc";

import { publicPageHeaders } from "./-public-cache";

export const Route = createFileRoute("/vehicles_/$vehicleId")({
  // the model is fetched on the server too, so the title and description are in the HTML;
  // a removed model answers a real 404 (an off-sale one still has its page)
  loader: async ({ context, params }) => {
    const [{ vehicle, others, plans }, content] = await Promise.all([
      context.queryClient.ensureQueryData(
        orpc.site.vehicles.byId.queryOptions({ input: { id: params.vehicleId } }),
      ),
      // already in the cache from the root route; here for the page's description
      loadSiteContent(context.queryClient),
    ]);
    if (!vehicle) throw notFound();
    return { vehicle, others, plans, page: content.vehiclePage };
  },
  notFoundComponent: VehicleNotFound,
  // link previews show the model's photo, price and staff's tagline; JSON-LD gives search
  // engines the Product/Car with its Offer (price, availability) and the breadcrumb
  head: ({ loaderData }) => {
    const vehicle = loaderData?.vehicle;
    const page = loaderData?.page;
    if (!vehicle || !page) return { meta: [{ title: "جزئیات خودرو | تابان خودرو" }] };
    // a price on request has no number for the description
    const listed = platePriceTitle(vehicle.price);
    const description = [
      vehicle.tagline
        ? `${vehicle.tagline.replace(/[.!؟]+$/, "")}.`
        : `${vehicle.name} مدل ${faPlainNumber(vehicle.year)}، صفر کیلومتر.`,
      listed && `قیمت ${primaryPlateText(vehicle)}: ${listed}.`,
      fillVehicleText(page.metaDescription, vehicle, page.anyBranch),
    ]
      .filter(Boolean)
      .join(" ");
    return {
      meta: [
        ...pageMeta(vehicleTitle(vehicle), description, vehicle.image || undefined),
        // a model page is a product, not the site's own page (the root route's default)
        { property: "og:type", content: "product" },
        { "script:ld+json": vehicleJsonLd(siteUrl, vehicle, description) },
        {
          "script:ld+json": breadcrumbJsonLd(siteUrl, [
            { name: "خانه", path: "/" },
            { name: "خودروها", path: "/vehicles" },
            { name: vehicle.name, path: `/vehicles/${vehicle.id}` },
          ]),
        },
      ],
    };
  },
  headers: publicPageHeaders,
  component: VehicleDetailPage,
});

const TITLE_SUFFIX = " | تابان خودرو";
/** گوگل عنوان را حدود همین‌جا می‌برد؛ بلندتر از این یعنی دم عنوان در نتیجه دیده نمی‌شود */
const TITLE_MAX = 60;

/**
 * عنوان صفحه‌ی هر مدل. «قیمت و مشخصات» همان چیزی است که خریدار پیش از نام مدل جستجو می‌کند و
 * سال مدل جستجوی بعدی اوست. نام لاتین هم می‌آید چون خریدار «Toyota Camry» را هم جستجو می‌کند،
 * و بر تیپ خودرو اولویت دارد: تیپ در فهرست گاهی یک خط بازاریابی است («Premier — GCC / UAE»).
 * از این چهار شکل، نخستینی که از مرز عنوان بلندتر نشود انتخاب می‌شود.
 */
function vehicleTitle(vehicle: { name: string; nameLatin: string; trim: string; year: number }) {
  const year = faPlainNumber(vehicle.year);
  const latin = vehicle.nameLatin ? `(${vehicle.nameLatin})` : "";
  const forms = [
    `قیمت و مشخصات ${vehicle.name} ${latin} ${vehicle.trim} ${year}`,
    `قیمت و مشخصات ${vehicle.name} ${latin} ${year}`,
    `قیمت و مشخصات ${vehicle.name} ${vehicle.trim} ${year}`,
    `قیمت و مشخصات ${vehicle.name} ${year}`,
  ].map((form) => form.replace(/\s+/g, " ").trim());
  const fits = forms.find((form) => form.length + TITLE_SUFFIX.length <= TITLE_MAX);
  return `${fits ?? forms[forms.length - 1]}${TITLE_SUFFIX}`;
}

const sections = [
  ["intro", "معرفی"],
  ["specs", "مشخصات فنی"],
  ["features", "امکانات"],
  ["terms", "شرایط فروش"],
  ["compare", "مقایسه"],
] as const;

/** the staff texts of the vehicle page (بلوک «صفحه هر خودرو») and a filler of their placeholders for this model */
function useVehicleCopy(vehicle: SiteVehicle) {
  const { vehiclePage } = useSiteContent();
  const fill = (template: string) => fillVehicleText(template, vehicle, vehiclePage.anyBranch);
  return { copy: vehiclePage, fill };
}

function VehicleNotFound() {
  const { vehiclePage } = useSiteContent();
  return (
    <NotFoundPage
      title={vehiclePage.notFoundTitle}
      description={vehiclePage.notFoundText}
      icon={CarAlertIcon}
    />
  );
}

/**
 * the plates this model is sold with (priced or on request), national first; never empty (a
 * model is sold with at least one)
 */
const platesOf = (vehicle: SiteVehicle) =>
  plateKinds.filter((kind) => plateKindPrice(vehicle, kind) !== null);

/** a link to the buying guide (/guide): steps, documents and the two plates */
const guideLink =
  "w-fit rounded-sm text-sm font-bold text-primary underline underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * the plate the visitor prices the model with: drives the plans, the calculator and the
 * request. `price` is toman, or PRICE_INQUIRY when that plate is priced on request.
 */
type PlateChoice = {
  plate: PlateKind;
  price: SiteVehicle["price"];
  setPlate: (plate: PlateKind) => void;
};

/** what the request buttons say: a plate priced on request asks for the price, not a purchase */
const requestLabel = (vehicle: SiteVehicle, choice: PlateChoice) =>
  !vehicle.available
    ? "خبرم کن"
    : priceAmount(choice.price) === null
      ? "درخواست استعلام قیمت"
      : "درخواست خرید";

function VehicleDetailPage() {
  const { vehicle, others, plans } = Route.useLoaderData();
  return <VehicleDetailContent key={vehicle.id} vehicle={vehicle} others={others} plans={plans} />;
}

function VehicleDetailContent({
  vehicle,
  others,
  plans,
}: {
  vehicle: SiteVehicle;
  others: SiteVehicle[];
  plans: SitePlan[];
}) {
  const { copy, fill } = useVehicleCopy(vehicle);
  const ctaLink = menuLinks(copy.ctaLink)[0];
  const guideText = platesOf(vehicle).length > 1 ? copy.guidePlatesLink : copy.guideLink;
  const [plan, setPlan] = useState("");
  const [plate, setPlate] = useState<PlateKind>(primaryPlateKind(vehicle));
  const choice: PlateChoice = {
    plate,
    price: plateKindPrice(vehicle, plate) ?? vehicle.price,
    setPlate,
  };

  const choosePlan = (title: string) => {
    setPlan(title);
    document.getElementById("request")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const navSections = [...sections, ["request", requestLabel(vehicle, choice)] as const];

  return (
    // room at the bottom for the fixed purchase bar (+ the site's mobile bar below md)
    <main className="min-h-[100dvh] bg-background pb-40 text-foreground md:pb-20 lg:[@media(min-height:50rem)]:pb-0">
      <SiteHeader />

      <section className="border-b bg-muted/45">
        <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 sm:py-10">
          <nav aria-label="مسیر صفحه" className="text-sm text-muted-foreground">
            <a href="/" className="hover:text-foreground">
              خانه
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <Link to="/vehicles" className="hover:text-foreground">
              خودروها
            </Link>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span className="font-bold text-foreground">{vehicle.name}</span>
          </nav>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-black sm:text-4xl">
                {vehicle.name}{" "}
                <span className="text-xl font-bold text-muted-foreground sm:text-2xl">
                  {vehicle.trim}
                </span>
              </h1>
              {/* همان مدل با حروف لاتین: خریدار «Toyota Camry» را هم جستجو می‌کند */}
              {vehicle.nameLatin ? (
                <p className="mt-1 text-sm font-bold tracking-wide text-muted-foreground">
                  {vehicle.nameLatin}
                </p>
              ) : null}
              <p className="mt-2 max-w-[60ch] leading-7 text-muted-foreground">{vehicle.tagline}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {vehicle.available ? null : <Badge variant="destructive">فعلاً موجود نیست</Badge>}
                <Badge>{vehicle.badge}</Badge>
                <Badge variant="secondary">مدل {faPlainNumber(vehicle.year)}</Badge>
                <Badge variant="secondary">{bodyLabels[vehicle.body]}</Badge>
                <Badge variant="secondary">{fuelLabels[vehicle.fuel]}</Badge>
              </div>
            </div>
            <div className="grid gap-2 sm:justify-items-end">
              {/* هر دو قیمت وقتی مدل با هر دو پلاک فروخته می‌شود؛ پلاک انتخاب‌شده پررنگ‌تر */}
              <dl className="flex flex-wrap gap-x-8 gap-y-3 text-start sm:justify-end sm:text-end">
                {platesOf(vehicle).map((kind) => (
                  <div key={kind}>
                    <dt className="text-sm text-muted-foreground">
                      قیمت فعلی نقدی با {plateKindLabels[kind]}
                    </dt>
                    <dd
                      className={cn(
                        "font-black transition-colors",
                        kind === plate
                          ? "text-2xl sm:text-3xl"
                          : "text-xl text-muted-foreground sm:text-2xl",
                      )}
                      title={platePriceTitle(plateKindPrice(vehicle, kind))}
                    >
                      {platePriceWords(plateKindPrice(vehicle, kind))}
                    </dd>
                  </div>
                ))}
              </dl>
              {/* تاریخ قیمت از خود کاتالوگ (هر بار که قیمتی عوض شود) و توضیح آن از مدیریت محتوا */}
              <p className="max-w-[46ch] text-xs leading-6 text-muted-foreground sm:text-end">
                {priceAmount(vehicle.price) === null ? (
                  // هیچ پلاکی قیمت عددی ندارد: تاریخ و توضیح قیمت معنایی ندارد
                  copy.priceInquiry
                ) : (
                  <>
                    <span className="font-bold text-foreground">
                      به‌روزرسانی قیمت: {faDate(vehicle.priceUpdatedAt)}
                    </span>
                    {copy.priceNote ? (
                      <>
                        {SEP}
                        {fill(copy.priceNote)}
                      </>
                    ) : null}
                  </>
                )}
              </p>
              {guideText ? (
                <Link to="/guide" className={guideLink}>
                  {guideText}
                </Link>
              ) : null}
              {/* روی گوشی و تبلت دکمه‌ی تست‌درایو ته صفحه‌ای ۹۰۰۰ پیکسلی بود؛ این‌جا هم در دسترس است
                  (روی دسکتاپ کارت خرید کنار صفحه همان را دارد) */}
              {vehicle.available ? (
                <div className="lg:hidden">
                  <TestDriveDialog vehicleId={vehicle.id} vehicleName={vehicle.name} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <nav
        aria-label="بخش‌های صفحه"
        className="sticky top-16 z-30 border-b bg-background/95 backdrop-blur-xl lg:top-20"
      >
        <div className="mx-auto flex max-w-[1400px] gap-1 overflow-x-auto px-4 py-2 sm:px-6">
          {navSections.map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className={cn(
                buttonVariants({ variant: "ghost", size: "sm" }),
                "shrink-0 rounded-full",
              )}
            >
              {label}
            </a>
          ))}
        </div>
      </nav>

      <section className="py-8 sm:py-12">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0">
            <MediaGallery vehicle={vehicle} />
            <QuickSpecs vehicle={vehicle} />
            <IntroSection vehicle={vehicle} />
            <SpecsSection vehicle={vehicle} />
            <FeaturesSection vehicle={vehicle} />
            <TermsSection vehicle={vehicle} plans={plans} choice={choice} onRequest={choosePlan} />
            <CompareSection vehicle={vehicle} others={others} />
          </div>

          <PurchaseAside vehicle={vehicle} plans={plans} plan={plan} choice={choice} />
        </div>
      </section>

      <section className="border-y bg-primary py-14 text-primary-foreground sm:py-16">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-4 sm:flex-row sm:items-center sm:px-6">
          <div>
            <h2 className="text-2xl font-black sm:text-3xl">{fill(copy.ctaTitle)}</h2>
            {copy.ctaText ? (
              <p className="mt-2 text-primary-foreground/75">{fill(copy.ctaText)}</p>
            ) : null}
          </div>
          {ctaLink ? (
            <a
              {...linkAttrs(ctaLink)}
              className={cn(buttonVariants({ variant: "secondary", size: "lg" }), "shrink-0")}
            >
              {ctaLink.label}
            </a>
          ) : null}
        </div>
      </section>

      <SiteFooter />
      <PurchaseBar vehicle={vehicle} choice={choice} />
      <MobileNavigation />
    </main>
  );
}

/**
 * Fixed bottom bar with the chosen plate's price, a jump to the request form and the
 * branch's phone. Hidden only where the sticky purchase card fits (tall desktop); on
 * phones it sits on top of the site's mobile bar.
 */
function PurchaseBar({ vehicle, choice }: { vehicle: SiteVehicle; choice: PlateChoice }) {
  return (
    <div className="fixed inset-x-0 bottom-[calc(4rem+1px+env(safe-area-inset-bottom))] z-40 border-t bg-background/95 backdrop-blur-xl md:bottom-0 md:pb-[env(safe-area-inset-bottom)] lg:[@media(min-height:50rem)]:hidden">
      <div className="mx-auto flex max-w-[1400px] items-center gap-2 px-4 py-2 sm:px-6">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-muted-foreground">
            {vehicle.available
              ? `قیمت نقدی با ${plateKindLabels[choice.plate]}`
              : `${vehicle.name}: فعلاً موجود نیست`}
          </p>
          {vehicle.available ? (
            <p className="truncate font-black" title={platePriceTitle(choice.price)}>
              {platePriceWords(choice.price)}
            </p>
          ) : null}
        </div>
        {vehicle.branchPhone ? (
          <a
            href={telHref(vehicle.branchPhone)}
            className={cn(buttonVariants({ variant: "outline" }), "h-11 shrink-0 rounded-xl")}
          >
            <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} data-icon="inline-start" />
            تماس
          </a>
        ) : null}
        <a href="#request" className={cn(buttonVariants(), "h-11 shrink-0 rounded-xl px-4")}>
          {requestLabel(vehicle, choice)}
        </a>
      </div>
    </div>
  );
}

type MediaItem = { kind: "image"; src: string } | { kind: "video"; src: string; title: string };

function MediaGallery({ vehicle }: { vehicle: SiteVehicle }) {
  const media = useMemo<MediaItem[]>(
    () => [
      { kind: "image", src: vehicle.image },
      ...vehicle.gallery.map((src): MediaItem => ({ kind: "image", src })),
      ...(vehicle.video.src
        ? [{ kind: "video" as const, src: vehicle.video.src, title: vehicle.video.title }]
        : []),
    ],
    [vehicle],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const active = media[activeIndex] ?? media[0];
  const imageUrl = useImageUrl();

  return (
    <div>
      <div className="relative aspect-[16/9] overflow-hidden rounded-2xl border bg-muted">
        {active.kind === "image" ? (
          <Image
            key={active.src}
            src={active.src}
            alt={`${vehicle.name} ${vehicle.trim}`}
            fill
            // the first photo is the page's largest paint: fetched with the document
            preload={activeIndex === 0}
            sizes="(max-width: 1024px) 100vw, 60vw"
            className="object-cover"
          />
        ) : (
          <video
            key={active.src}
            src={active.src}
            controls
            preload="none"
            poster={imageUrl(vehicle.image, 1200)}
            title={active.title}
            className="size-full bg-black object-contain"
          >
            <track kind="captions" />
          </video>
        )}
      </div>
      <div
        role="tablist"
        aria-label="گالری تصاویر و ویدیو"
        className="mt-3 flex gap-2 overflow-x-auto pb-1"
      >
        {media.map((item, index) => (
          <button
            key={`${item.kind}-${item.src}`}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            aria-label={
              item.kind === "video"
                ? item.title || "ویدیوی معرفی"
                : `تصویر ${faPlainNumber(index + 1)}`
            }
            onClick={() => setActiveIndex(index)}
            className={cn(
              "relative aspect-[4/3] w-24 shrink-0 overflow-hidden rounded-xl border transition-shadow sm:w-28",
              index === activeIndex ? "ring-2 ring-ring" : "opacity-80 hover:opacity-100",
            )}
          >
            <Image
              src={item.kind === "video" ? vehicle.image : item.src}
              alt=""
              width={112}
              height={84}
              className="size-full object-cover"
            />
            {item.kind === "video" && (
              <span className="absolute inset-0 grid place-items-center bg-black/45 text-white">
                <HugeiconsIcon icon={PlayIcon} strokeWidth={1.8} className="size-6" />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function QuickSpecs({ vehicle }: { vehicle: SiteVehicle }) {
  const items = [
    { icon: DashboardSpeed01Icon, label: "حداکثر قدرت", value: vehicle.specs.power },
    { icon: Timer01Icon, label: "شتاب ۰ تا ۱۰۰", value: vehicle.specs.acceleration },
    {
      icon: vehicle.fuel === "electric" ? AutomotiveBattery01Icon : FuelStationIcon,
      label: vehicle.fuel === "electric" ? "برد حرکتی" : "مصرف ترکیبی",
      value: vehicle.specs.consumption,
    },
    { icon: Wrench02Icon, label: "جعبه‌دنده", value: vehicle.specs.gearbox },
  ];

  return (
    <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map((item) => (
        <div key={item.label} className="rounded-2xl border bg-card p-4">
          <div className="flex gap-2 text-center items-center">
            <HugeiconsIcon icon={item.icon} strokeWidth={1.6} className="size-6 text-primary" />
            <p className="mt-3 text-xs text-muted-foreground">{item.label}</p>
          </div>
          <p className="mt-1 text-sm font-black leading-6">{item.value}</p>
        </div>
      ))}
    </div>
  );
}

function IntroSection({ vehicle }: { vehicle: SiteVehicle }) {
  return (
    <section id="intro" aria-labelledby="intro-title" className="mt-10 scroll-mt-32">
      <h2 id="intro-title" className="text-2xl font-black">
        معرفی خودرو
      </h2>
      <p className="mt-4 leading-8 text-muted-foreground">{vehicle.intro}</p>
      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {vehicle.highlights.map((highlight) => (
          <li key={highlight} className="flex items-start gap-2.5 rounded-xl border bg-card p-3.5">
            <HugeiconsIcon
              icon={CheckmarkCircle02Icon}
              strokeWidth={1.8}
              className="mt-0.5 size-5 shrink-0 text-primary"
            />
            <span className="text-sm font-bold leading-6">{highlight}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SpecsSection({ vehicle }: { vehicle: SiteVehicle }) {
  return (
    <section id="specs" aria-labelledby="specs-title" className="mt-12 scroll-mt-32">
      <h2 id="specs-title" className="text-2xl font-black">
        مشخصات فنی
      </h2>
      <div className="mt-5 grid gap-5">
        {specGroups.map((group) => (
          <div key={group.title} className="overflow-hidden rounded-2xl border bg-card">
            <h3 className="border-b bg-muted/45 px-5 py-3.5 font-black">{group.title}</h3>
            <Table>
              <TableBody>
                {group.keys.map((key) => (
                  <TableRow key={key}>
                    <TableCell className="w-1/3 whitespace-normal px-5 text-muted-foreground">
                      {specLabels[key]}
                    </TableCell>
                    <TableCell className="whitespace-normal px-5 font-bold">
                      {vehicle.specs[key]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturesSection({ vehicle }: { vehicle: SiteVehicle }) {
  return (
    <section id="features" aria-labelledby="features-title" className="mt-12 scroll-mt-32">
      <h2 id="features-title" className="text-2xl font-black">
        امکانات و آپشن‌ها
      </h2>
      <Accordion
        defaultValue={vehicle.features.map((group) => group.group)}
        className="mt-4 rounded-2xl border bg-card px-5"
      >
        {vehicle.features.map((group) => (
          <AccordionItem key={group.group} value={group.group}>
            <AccordionTrigger className="text-base font-black">{group.group}</AccordionTrigger>
            <AccordionContent>
              <ul className="grid gap-2.5 sm:grid-cols-2">
                {group.items.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <HugeiconsIcon
                      icon={CheckmarkCircle02Icon}
                      strokeWidth={1.8}
                      className="mt-0.5 size-4.5 shrink-0 text-primary"
                    />
                    <span className="leading-6">{item}</span>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

/** انتخاب پلاک وقتی مدل با هر دو پلاک فروخته می‌شود؛ طرح‌ها، ماشین‌حساب و درخواست خرید از آن پیروی می‌کنند */
function PlateToggle({
  vehicle,
  choice,
  withPrices = false,
  className,
}: {
  vehicle: SiteVehicle;
  choice: PlateChoice;
  /** each option also shows its price (the purchase aside) */
  withPrices?: boolean;
  className?: string;
}) {
  const plates = platesOf(vehicle);
  if (plates.length < 2) return null;
  return (
    <DirectionProvider direction="rtl">
      <ToggleGroup
        aria-label="نوع پلاک"
        variant="outline"
        spacing={0}
        value={[choice.plate]}
        onValueChange={(value) => {
          // pressing the chosen plate again would leave none: keep it
          const next = plateKinds.find((kind) => kind === value[0]);
          if (next) choice.setPlate(next);
        }}
        className={cn("w-full", className)}
      >
        {plates.map((kind) => (
          <ToggleGroupItem
            key={kind}
            value={kind}
            className={cn(
              "flex-1 aria-pressed:bg-primary aria-pressed:text-primary-foreground",
              withPrices && "h-auto flex-col gap-0.5 py-2",
            )}
          >
            <span>{plateKindLabels[kind]}</span>
            {withPrices ? (
              <span className="text-xs font-black">
                {platePriceWords(plateKindPrice(vehicle, kind))}
              </span>
            ) : null}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </DirectionProvider>
  );
}

function TermsSection({
  vehicle,
  plans,
  choice,
  onRequest,
}: {
  vehicle: SiteVehicle;
  /** طرح‌های فعال همین مدل از مدیریت محتوا (زبانه «طرح‌های فروش و اقساط»)، به ترتیب */
  plans: SitePlan[];
  choice: PlateChoice;
  onRequest: (plan: string) => void;
}) {
  // مراحل خرید و مدارک لازم، مشترک بین همه مدل‌ها، از مدیریت محتوا
  const { finance } = useSiteContent();
  const { copy, fill } = useVehicleCopy(vehicle);
  const purchaseSteps = lines(copy.purchaseSteps);
  const requiredDocuments = lines(copy.requiredDocuments);
  const { annualRate } = financeCalculator(finance);
  // null = the chosen plate is priced on request: the plans have no amounts to show
  const price = priceAmount(choice.price);

  return (
    <section id="terms" aria-labelledby="terms-title" className="mt-12 scroll-mt-32">
      <h2 id="terms-title" className="text-2xl font-black">
        شرایط فروش و مراحل خرید
      </h2>
      {copy.termsIntro ? (
        <p className="mt-3 leading-7 text-muted-foreground">{fill(copy.termsIntro)}</p>
      ) : null}

      {plans.length === 0 ? null : price === null ? (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm leading-6 text-muted-foreground">
            قیمت با {plateKindLabels[choice.plate]}:{" "}
            <span className="font-bold text-foreground">{copy.priceInquiry}</span>
          </p>
          <PlateToggle vehicle={vehicle} choice={choice} className="sm:w-auto" />
        </div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              مبلغ‌ها بر اساس قیمت نقدی با {plateKindLabels[choice.plate]}:{" "}
              <span className="font-bold text-foreground" title={faToman(price)}>
                {faTomanWords(price)}
              </span>
            </p>
            <PlateToggle vehicle={vehicle} choice={choice} className="sm:w-auto" />
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {plans.map((plan) => {
              const down = Math.round((price * plan.downPercent) / 100);
              const monthly = planMonthly(price, plan);
              return (
                <div
                  key={plan.id}
                  className={cn(
                    "relative flex flex-col rounded-2xl border bg-card p-5",
                    plan.recommended && "ring-2 ring-primary",
                  )}
                >
                  {plan.recommended && (
                    <Badge className="absolute -top-3 start-4">پیشنهاد کارشناسان</Badge>
                  )}
                  <h3 className="font-black">{plan.title}</h3>
                  <p className="mt-4 text-xs text-muted-foreground">
                    {plan.months === 0 ? "پرداخت کامل" : "پیش‌پرداخت"}
                  </p>
                  <p className="mt-1 font-black">
                    {faNumber(down)} <span className="text-xs font-normal">تومان</span>
                  </p>
                  {plan.months > 0 && (
                    <>
                      <p className="mt-3 text-xs text-muted-foreground">
                        قسط ماهانه ({faPlainNumber(plan.months)} ماهه)
                      </p>
                      <p className="mt-1 font-black text-primary">
                        {faNumber(monthly)} <span className="text-xs font-normal">تومان</span>
                      </p>
                      {/* نرخ جدای طرح؛ نرخ عمومی سایت در زیرنویس پایین آمده است */}
                      {plan.annualRate !== annualRate ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          با سود سالانه {faPercent(plan.annualRate)}
                        </p>
                      ) : null}
                    </>
                  )}
                  {/* grow: دکمه‌ها هم‌تراز می‌مانند حتی وقتی توضیح طرح خالی است */}
                  <p className="mt-4 grow text-sm leading-6 text-muted-foreground">{plan.note}</p>
                  <Button
                    variant={plan.recommended ? "default" : "outline"}
                    className="mt-4 w-full rounded-xl"
                    onClick={() => onRequest(plan.title)}
                  >
                    انتخاب این طرح
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs leading-6 text-muted-foreground">
            {rateNote(annualRate, finance.disclaimer)}
          </p>
        </>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border bg-card p-5">
          <h3 className="font-black">مراحل خرید</h3>
          <ol className="mt-4 grid gap-3">
            {purchaseSteps.map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-black text-primary">
                  {faPlainNumber(index + 1)}
                </span>
                <span className="pt-0.5 text-sm leading-6">{step}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-2xl border bg-card p-5">
          <h3 className="font-black">مدارک لازم</h3>
          <ul className="mt-4 grid gap-3">
            {requiredDocuments.map((doc, index) => (
              <li key={index} className="flex items-start gap-2.5">
                <HugeiconsIcon
                  icon={CheckmarkCircle02Icon}
                  strokeWidth={1.8}
                  className="mt-0.5 size-5 shrink-0 text-primary"
                />
                <span className="text-sm leading-6">{doc}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {copy.guideTermsLink ? (
        <Link to="/guide" className={cn(guideLink, "mt-4 block")}>
          {copy.guideTermsLink}
        </Link>
      ) : null}
    </section>
  );
}

function CompareSection({ vehicle, others }: { vehicle: SiteVehicle; others: SiteVehicle[] }) {
  const defaultRival = others.find((item) => item.body === vehicle.body) ?? others[0] ?? null;
  const [rivalId, setRivalId] = useState<string | null>(null);
  const rival = others.find((item) => item.id === (rivalId ?? defaultRival?.id)) ?? defaultRival;

  if (!rival) return null;

  const rows: { label: string; a: string; b: string }[] = [
    {
      label: "قیمت نقدی",
      a: `${platePriceWords(vehicle.price)} ${primaryPlateText(vehicle)}`,
      b: `${platePriceWords(rival.price)} ${primaryPlateText(rival)}`,
    },
    {
      label: "اقساط ماهانه از",
      a: monthlyFromText(vehicle),
      b: monthlyFromText(rival),
    },
    {
      label: "سال / تیپ",
      a: `${faPlainNumber(vehicle.year)} ${vehicle.trim}`,
      b: `${faPlainNumber(rival.year)} ${rival.trim}`,
    },
    { label: "نوع بدنه", a: bodyLabels[vehicle.body], b: bodyLabels[rival.body] },
    { label: "نوع سوخت", a: fuelLabels[vehicle.fuel], b: fuelLabels[rival.fuel] },
    ...specKeys.map((key) => ({
      label: specLabels[key],
      a: vehicle.specs[key],
      b: rival.specs[key],
    })),
  ];

  return (
    <section id="compare" aria-labelledby="compare-title" className="mt-12 scroll-mt-32">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="compare-title" className="text-2xl font-black">
          مقایسه با سایر خودروها
        </h2>
        {/* form-kit-exempt: انتخاب رقیب جدول مقایسه، فرمی ارسال نمی‌شود */}
        <NativeSelect
          aria-label="انتخاب خودرو برای مقایسه"
          value={rival.id}
          onChange={(event) => setRivalId(event.target.value)}
        >
          {others.map((item) => (
            <NativeSelectOption key={item.id} value={item.id}>
              {item.name} {item.trim}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border bg-card">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b bg-muted/45">
              <th scope="col" className="px-5 py-4 text-start font-bold text-muted-foreground">
                ویژگی
              </th>
              <th scope="col" className="px-5 py-4 text-start">
                <span className="flex items-center gap-2">
                  <span className="font-black">{vehicle.name}</span>
                  <Badge variant="secondary">همین خودرو</Badge>
                </span>
              </th>
              <th scope="col" className="px-5 py-4 text-start font-black">
                {rival.name}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b last:border-b-0">
                <th
                  scope="row"
                  className="px-5 py-3.5 text-start font-normal text-muted-foreground"
                >
                  {row.label}
                </th>
                <td className="px-5 py-3.5 font-bold">{row.a}</td>
                <td className="px-5 py-3.5">{row.b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link
          to="/vehicles/$vehicleId"
          params={{ vehicleId: rival.id }}
          className={cn(buttonVariants({ variant: "outline" }), "rounded-xl")}
        >
          <HugeiconsIcon icon={GitCompareIcon} strokeWidth={1.8} data-icon="inline-start" />
          مشاهده جزئیات {rival.name}
        </Link>
        <Link
          to="/compare"
          search={{ ids: `${vehicle.id},${rival.id}` }}
          className={cn(buttonVariants({ variant: "ghost" }), "text-primary")}
        >
          مقایسه کامل در صفحه مقایسه
        </Link>
        <Link to="/vehicles" className={cn(buttonVariants({ variant: "ghost" }), "text-primary")}>
          دیدن همه خودروها
        </Link>
      </div>
    </section>
  );
}

/** ثبت درخواست خرید: یک سرنخ فروش با همین خودرو برای کارشناس فروش ساخته می‌شود */
const purchaseRequestSchema = z.object({
  name: requiredText("نام و نام خانوادگی", { max: 60 }),
  phone: mobileText(),
  color: z.string(),
});

function PurchaseRequestForm({
  vehicle,
  plan,
  choice,
}: {
  vehicle: SiteVehicle;
  plan: string;
  choice: PlateChoice;
}) {
  const { plate } = choice;
  const { copy, fill } = useVehicleCopy(vehicle);
  const followUp = vehicle.available ? copy.requestFollowUp : copy.unavailableFollowUp;
  const [error, setError] = useState("");
  // the number of the accepted request: its confirmation stays in place of the form
  const [sentTo, setSentTo] = useState("");

  const mutation = useMutation(
    orpc.site.requestConsult.mutationOptions({
      onSuccess: (result) => setError(result.ok ? "" : result.reason),
      onError: () => setError("ثبت درخواست انجام نشد؛ لطفاً دوباره تلاش کنید."),
    }),
  );

  const form = useAppForm({
    defaultValues: { name: "", phone: "", color: "" },
    validators: { onBlur: purchaseRequestSchema },
    onSubmit: async ({ value, formApi }) => {
      setError("");
      const phone = toEnDigits(value.phone.trim());
      const result = await mutation
        .mutateAsync({
          name: value.name.trim(),
          phone,
          topic: "buy",
          time: "any",
          note: [
            vehicle.available ? "" : "خودرو فعلاً ناموجود است؛ خبر موجود شدن یا پیشنهاد مشابه",
            priceAmount(choice.price) === null ? `استعلام قیمت با ${plateKindLabels[plate]}` : "",
            plan ? `طرح انتخابی: ${plan}` : "",
          ]
            .filter(Boolean)
            .join("\n"),
          vehicleId: vehicle.id,
          plateKind: plate,
          attribution: readAttribution(),
          color: value.color,
        })
        .catch(() => undefined);
      if (!result?.ok) return;
      formApi.reset();
      setSentTo(phone);
    },
  });

  if (sentTo) {
    return (
      <div id="request" className="mt-6 scroll-mt-32">
        <RequestSuccess
          phone={sentTo}
          followUp={followUp ? fill(followUp) : undefined}
          onAgain={() => setSentTo("")}
        />
      </div>
    );
  }

  return (
    <form.AppForm>
      <form.Form id="request" className="mt-6 grid gap-2.5 scroll-mt-32">
        {!vehicle.available && copy.unavailableNotice ? (
          <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm leading-6 text-destructive">
            {fill(copy.unavailableNotice)}
          </p>
        ) : null}
        {plan ? (
          <p className="rounded-lg bg-primary/10 px-3 py-2 text-xs font-bold text-primary">
            طرح انتخابی: {plan}، {plateKindLabels[plate]}
          </p>
        ) : null}
        <form.AppField name="name">
          {(field) => (
            <field.TextField label="نام و نام خانوادگی" autoComplete="name" maxLength={60} />
          )}
        </form.AppField>
        <form.AppField name="phone">
          {(field) => (
            <field.TextField
              label="شماره موبایل"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              dir="ltr"
              className="text-start"
              maxLength={14}
              placeholder="مثلاً ۰۹۱۲۱۲۳۴۵۶۷"
            />
          )}
        </form.AppField>
        {vehicle.colors.length > 0 ? (
          <form.AppField name="color">
            {(field) => (
              <field.SelectField
                label="رنگ دلخواه"
                placeholder="فرقی ندارد"
                options={vehicle.colors.map((color) => ({ value: color.name, label: color.name }))}
              />
            )}
          </form.AppField>
        ) : null}
        <FormAlert message={error} className="text-xs" />
        <form.SubmitButton size="lg" className="w-full rounded-xl">
          {vehicle.available ? `ثبت ${requestLabel(vehicle, choice)}` : "خبرم کن"}
        </form.SubmitButton>
        <a href="#compare" className={cn(buttonVariants({ variant: "ghost" }), "text-primary")}>
          {vehicle.available ? "مقایسه با رقبا" : "خودروهای مشابه موجود"}
        </a>
      </form.Form>
    </form.AppForm>
  );
}

function PurchaseAside({
  vehicle,
  plans,
  plan,
  choice,
}: {
  vehicle: SiteVehicle;
  plans: SitePlan[];
  plan: string;
  choice: PlateChoice;
}) {
  const { copy, fill } = useVehicleCopy(vehicle);
  // null = قیمت پلاک انتخابی «استعلام» است: به‌جای عدد، متن استعلام و دکمه‌های درخواست و تماس می‌آید
  const amount = priceAmount(choice.price);
  // کمترین قسط همان طرح‌های پایین صفحه با قیمت پلاک انتخابی (همان فرمول سرور)؛ بدون طرح قسطی null
  const lowest = amount === null ? null : monthlyFrom(plans, amount);
  return (
    // sticky only where the whole card fits (not on 1366×768 laptops: the purchase bar
    // covers those); a card taller than the screen scrolls inside instead of being cut off
    <aside className="lg:h-fit lg:[@media(min-height:50rem)]:sticky lg:[@media(min-height:50rem)]:top-[8.75rem] lg:[@media(min-height:50rem)]:max-h-[calc(100dvh-9.75rem)] lg:[@media(min-height:50rem)]:overflow-y-auto lg:[@media(min-height:50rem)]:overscroll-contain lg:[@media(min-height:50rem)]:rounded-2xl">
      <div className="rounded-2xl border bg-card p-5">
        <p className="text-sm text-muted-foreground">
          قیمت نقدی با {plateKindLabels[choice.plate]}
        </p>
        <p className="mt-1 text-2xl font-black" title={platePriceTitle(choice.price)}>
          {platePriceWords(choice.price)}
        </p>
        {amount === null ? (
          <div className="mt-2 grid gap-3">
            <p className="text-sm leading-6 text-muted-foreground">{copy.priceInquiry}</p>
            <div className="flex flex-wrap gap-2">
              <a href="#request" className={cn(buttonVariants({ size: "sm" }), "rounded-xl")}>
                درخواست مشاوره
              </a>
              {vehicle.branchPhone ? (
                <a
                  href={telHref(vehicle.branchPhone)}
                  className={cn(buttonVariants({ variant: "outline", size: "sm" }), "rounded-xl")}
                >
                  <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} data-icon="inline-start" />
                  تماس با شعبه
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
        {lowest === null ? null : (
          <p className="mt-2 text-sm text-muted-foreground">
            اقساط از {faNumber(lowest)} تومان در ماه
          </p>
        )}
        <PlateToggle vehicle={vehicle} choice={choice} withPrices className="mt-4" />

        <div className="mt-5 grid gap-3 text-sm">
          {copy.asideDelivery ? (
            <div className="flex items-center gap-2.5">
              <HugeiconsIcon
                icon={Calendar03Icon}
                strokeWidth={1.6}
                className="size-5 text-primary"
              />
              <span>{fill(copy.asideDelivery)}</span>
            </div>
          ) : null}
          {vehicle.available && copy.asideBranch ? (
            <div className="flex items-center gap-2.5">
              <HugeiconsIcon
                icon={Location01Icon}
                strokeWidth={1.6}
                className="size-5 text-primary"
              />
              <span>{fill(copy.asideBranch)}</span>
            </div>
          ) : null}
          <div className="flex items-center gap-2.5">
            <HugeiconsIcon
              icon={SecurityCheckIcon}
              strokeWidth={1.6}
              className="size-5 text-primary"
            />
            <span>{vehicle.warranty}</span>
          </div>
        </div>

        <Separator className="my-5" />

        <p className="flex items-center gap-2 text-sm font-bold">
          <HugeiconsIcon icon={PaintBoardIcon} strokeWidth={1.6} className="size-5 text-primary" />
          رنگ‌بندی
        </p>
        <ul className="mt-3 grid gap-2">
          {vehicle.colors.map((color) => (
            <li key={color.name} className="flex items-center gap-2.5 text-sm">
              <span
                aria-hidden="true"
                className="size-4 rounded-full border"
                style={{ backgroundColor: color.value }}
              />
              {color.name}
            </li>
          ))}
        </ul>

        <PurchaseRequestForm vehicle={vehicle} plan={plan} choice={choice} />
        {vehicle.available ? (
          <TestDriveDialog vehicleId={vehicle.id} vehicleName={vehicle.name} />
        ) : null}
      </div>
    </aside>
  );
}
